/* eslint-disable react/display-name */
import { useQueryClient } from "@trueskin-web/apis"
import { formatCurrency } from "@trueskin-web/functions"
import { useTranslation } from "@trueskin-web/translations"

import { plans } from "../resources"

const OrderSummaryDetails = ({ component: Component, orderSummary }) => {
  const { t } = useTranslation()

  const voucher = orderSummary.vouchers.find(
    ({ systemDefault, stackable }) => !(systemDefault || stackable)
  )

  const voucherDetails =
    orderSummary.occurrence !== "occ_once" && voucher
      ? {
          code: voucher.voucher.code,
          message: voucher.discountGroupVariant?.definition.confirmationMessage,
          error: voucher.errors[0]?.message,
        }
      : undefined

  const referralDetails =
    orderSummary.occurrence !== "occ_once" && orderSummary.referral
      ? {
          code: orderSummary.referral.code,
          message: orderSummary.referral.message,
        }
      : undefined

  const instalmentsDetails = {
    instalmentsCount: orderSummary.instalments.count,
    perInstalmentPrice: formatCurrency(
      Number.parseInt(orderSummary.values.perInstalment.total) / 100,
      orderSummary.values.perInstalment.currency
    ),
    instalmentsTotalPrice: formatCurrency(
      Number.parseInt(orderSummary.values.total) / 100,
      orderSummary.values.currency
    ),
  }

  const servicesOrder = ["other", "medical_service", "addon", "shipping"]
  const discountServices = orderSummary.lineItems.filter(
    (item) => item.type === "discount"
  )

  const purchaseDetails = {
    voucher: referralDetails || voucherDetails,

    presetId: orderSummary.preset.id,
    image: orderSummary.preset.details.image,
    title: orderSummary.preset.details.name,
    productTitle:
      orderSummary.instalments.count === 2
        ? t("TwoMonthsPresets.title", {
            count: orderSummary.instalments.count,
          })
        : orderSummary.preset.details.title,
    productDescription: orderSummary.preset.details.description,

    services: servicesOrder
      .reduce((acc, type) => {
        const item = orderSummary.lineItems.find((item) => item.type === type)
        if (!item) {
          return acc
        }
        return [...acc, item]
      }, [])
      .concat(discountServices)
      .map(({ amount, currency, ...item }) => {
        const presetTitle = plans[orderSummary.preset.id]?.title
        const presetDescription = plans[orderSummary.preset.id]?.description

        return {
          ...item,
          title:
            item.type === "other" && presetTitle ? presetTitle : item.title,
          description:
            item.type === "other" && presetDescription
              ? presetDescription
              : item.description,
          price: formatCurrency(Number.parseInt(amount) / 100, currency, 2),
        }
      }),

    totalPrice: formatCurrency(
      Number.parseInt(orderSummary.values.total) /
        orderSummary.instalments.count /
        100,
      orderSummary.values.currency,
      2
    ),

    totalWithoutDiscounts: formatCurrency(
      orderSummary.values.totalWithoutDiscounts /
        orderSummary.instalments.count /
        100,
      orderSummary.values.currency,
      2
    ),

    isSubscription: orderSummary.occurrence !== "occ_once",

    ...instalmentsDetails,
  }

  return <Component {...purchaseDetails} />
}

const withOrderSummary = (component) => () => {
  const queryClient = useQueryClient()

  const { data } = queryClient.getQueryData("currentOrderSummary")

  return <OrderSummaryDetails component={component} orderSummary={data} />
}

export default withOrderSummary
